import { sendLog } from './api/logger-api';
import { getIsEdge } from './configs/env';

export interface LogPayload {
  portal_alias?: string | null;
  document_name?: string | null;
  search_query?: string | null;
  stacktrace?: string | null;
  message?: string | null;
  error?: unknown;
  href?: string | null;
  // Workspace and portal identifiers
  workspace_id?: string | null;
  portal_id?: string | null;
  workspace_alias?: string | null;
  // Document and content identifiers
  kapp_id?: string | null;
  doc_guid?: string | null;
  guid?: string | null;
  page?: string | null;
  // User and session identifiers
  user_id?: string | null;
  session_id?: string | null;
  // Router and navigation
  router_props?: unknown;
  path?: string | null;
  // URL and API related
  alias_url?: string | null;
  auth_url?: string | null;
  url?: string | null;
  home_url?: string | null;
  video_section_url?: string | null;
  video_source_url?: string | null;
  video_text_url?: string | null;
  // Authentication and initialization
  user?: unknown;
  // Events and responses
  event_type?: string | null;
  error_detail?: string | null;
  ping_response?: unknown;
  // Document and file handling
  document_uri?: string | null;
  file_name?: string | null;
  element_id?: string | null;
  // TTS and speech synthesis
  error_type?: string | null;
  text?: string | null;
  voice?: string | null;
  lang?: string | null;
  cancel_reason?: unknown;
  // Categories and search
  categories?: unknown;
  // Network request fields
  api_endpoint?: string | null;
  request_method?: string | null;
  request_headers?: Record<string, string> | null;
  request_body?: any;
  base_url?: string | null;
  full_url?: string | null;

  // Network response fields
  response_status?: number | null;
  response_headers?: Record<string, string> | null;
  response_body?: any;
  api_response_time?: number | null;

  // EventSource fields
  message_count?: number | null;
  total_data_size?: number | null;
  event_detail?: string | null;

  // Additional context fields
  [key: string]: unknown;
}

class Logger {
  private async sendToAPI(level: 'info' | 'warning' | 'error', payload: LogPayload) {
    try {
      const loggerEnabled = (global as any)._loggerEnabled;
      if (loggerEnabled === false) {
        return;
      }

      const isEdge = getIsEdge();
      const token = (global as any).token;
      if (!token && !isEdge) {
        console.warn('Logger: No token available, skipping log send. Logger is enabled but logs cannot be sent without authentication.');
        // For non-edge, require token. For edge, backend doesn't check auth.
        return;
      }

      const dataWithHref = {
        ...payload,
        source: 'web-app',
        href: payload.href || window.location.href,
        timestamp: Date.now(),
      };

      // Use token if available, otherwise placeholder for edge
      await sendLog(level, dataWithHref, token || 'edge-client');
    } catch (error) {
      //TODO: store these in localStorage and sync later

      console.error('Logger: Failed to send log to API:', error);
    }
  }

  private formatConsoleMessage(level: string, payload: LogPayload): string {
    const timestamp = new Date().toISOString();
    const href = payload.href || window.location.href;

    let message = `[${timestamp}] [${level.toUpperCase()}]`;

    if (payload.message) {
      message += ` ${payload.message}`;
    }

    if (payload.portal_alias) {
      message += ` (portal_alias: ${payload.portal_alias})`;
    }

    if (payload.document_name) {
      message += ` (document_name: ${payload.document_name})`;
    }

    if (payload.search_query) {
      message += ` (search_query: ${payload.search_query})`;
    }

    message += ` (href: ${href})`;

    return message;
  }

  log(payload: LogPayload) {
    const consoleMessage = this.formatConsoleMessage('info', payload);
    console.log(consoleMessage, payload);

    this.sendToAPI('info', payload);
  }

  warn(payload: LogPayload) {
    const consoleMessage = this.formatConsoleMessage('warning', payload);
    console.warn(consoleMessage, payload);

    this.sendToAPI('warning', payload);
  }

  error(payload: LogPayload) {
    const consoleMessage = this.formatConsoleMessage('error', payload);
    console.error(consoleMessage, payload);

    if (payload.error && payload.error instanceof Error && !payload.stacktrace) {
      payload.stacktrace = payload.error.stack;
    }

    this.sendToAPI('error', payload);
  }
}

const logger = new Logger();

export default logger;
